import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { SWRConfiguration } from "swr";

import { FetcherResult } from "../api-client/fetcher";
import { ModifiedSWRResponse, useSwrGet } from "../api-client/useSwrGet";
import { useColumnSortFunc } from "../blocks/ColumnSort/useColumnSortFunc";
import { useColumnSortState } from "../blocks/ColumnSort/useColumnSortState";
import { usePricingAnalysisSetting } from "../blocks/PricingAnalysis/usePricingAnalysisSetting";
import { useNullableDealerAccountInfo } from "../stores/hooks/useNullableDealerAccountInfo";

const dateFields = new Set<string>([]);

const numericFields = new Set([
  "partNumber",
  "costPrice",
  "minPrice",
  "quantityBooked",
  "quantitySold",
  "yourPrice",
]);

export const useGetDealerComparisonPricesByTireSize = (
  tireSize: string,
  swrOptions: SWRConfiguration<FetcherResult<RawPayload>> = {}
): ModifiedSWRResponse<
  FetcherResult<RawPayload>,
  DealerComparisonPricesPayload
> => {
  const dealer = useNullableDealerAccountInfo();
  const setting = usePricingAnalysisSetting();
  const { t } = useTranslation();
  const sort = useColumnSortState("pricingAnalysis");
  const sortFunc = useColumnSortFunc("pricingAnalysis", {
    dateFields,
    numericFields,
  });

  const shouldFetch = !!tireSize && !!dealer && setting;

  const { data, ...rest } = useSwrGet<RawPayload>(
    shouldFetch
      ? "/retailSiteWebService/parts/tire/getDealerComparisonPricesByTireSize.php"
      : null,
    {
      dealerId: dealer?.dealerId || "",
      tireSize,
    },
    swrOptions
  );

  const mappedData = useMemo(() => {
    return (
      data &&
      Object.entries(data.partPriceMap || {})
        .map(([key, { comparisonPricingInfo, tireInfo }]) => {
          const yourPrice = parseFloat(
            comparisonPricingInfo.dealerPrice.my_price
          );

          const minPrice = parseFloat(
            comparisonPricingInfo.competitorPriceCalculation.minRetailPrice
          );

          const diffInPercent = ((yourPrice - minPrice) / minPrice) * 100;
          const diffAsStr = `${Math.abs(diffInPercent).toFixed(2)}%`;

          const isBestPrice = diffInPercent < 0;

          const summary = t(
            isBestPrice
              ? "pricingAnalysis.summaryLowerThen"
              : "pricingAnalysis.summaryHigherThen",
            { val: diffAsStr }
          );

          return {
            averagePrice: parseFloat(
              comparisonPricingInfo.competitorPriceCalculation.avgRetailPrice
            ),
            brand: tireInfo.manufacturer_name,
            costPrice: parseFloat(comparisonPricingInfo.dealerPrice.cost_price),
            isBestPrice,
            key,
            minPrice,
            name: tireInfo.model_name,
            partNumber: tireInfo.primary_part_number,
            quantityBooked: Number(tireInfo.booked_qty),
            quantitySold: Number(tireInfo.sales_qty),
            season: tireInfo.season,
            summary,
            tireSize: `${Number(tireInfo.width)}/${tireInfo.ratio}/R${Number(
              tireInfo.rim_diameter
            )}`,
            yourPrice,
          };
        })
        .sort(sortFunc)
    );
  }, [data, sort.field, sort.order, sortFunc]);

  return { ...rest, data: mappedData };
};

export type DealerComparisonPricesPayload = {
  averagePrice: number;
  brand: string;
  costPrice: number;
  isBestPrice: boolean;
  key: string;
  minPrice: number;
  name: string;
  partNumber: string;
  quantityBooked: number;
  quantitySold: number;
  season: string;
  summary: string;
  tireSize: string;
  yourPrice: number;
}[];

type RawPayload = {
  partPriceMap: PartPriceMap;
};

type TireInfo = {
  aaia_code: string;
  all_weather: string;
  booked_qty: number;
  colour: string;
  construct_code: string;
  description_code: string;
  discontinued: string;
  discontinued_time_stamp: string;
  dual_fitment_load_index: string;
  load_index: string;
  manufacturer_atd_connect_brand_code: string;
  manufacturer_group_code: string;
  manufacturer_image: string;
  manufacturer_name: string;
  manufacturer_primary_image_uploaded: string;
  manufacturer_primary_image_url: string;
  model_description: string;
  model_description_fr: string;
  model_image: string;
  model_manufacturer_url: string;
  model_name: string;
  model_primary_image_uploaded: string;
  model_primary_image_url: string;
  outer_diameter: string;
  ply_rating: string;
  primary_part_number: string;
  ratio: string;
  release_date: string;
  rim_diameter: string;
  run_flat: string;
  sales_qty: number;
  season: string;
  speed_rating: string;
  tire_id: string;
  tire_manufacturer_id: string;
  tire_model_id: string;
  tire_size_spec_id: string;
  tire_vehicle_class: string;
  tread_depth: string;
  treadlife_warranty_ca: string;
  treadlife_warranty_us: string;
  type: string;
  width: string;
};

type PriceInfo = {
  automation_brand_list: string;
  automation_type: string;
  booking_price: string;
  cost_price: string;
  is_tiered: string;
  last_updated_timestamp: string;
  list_price: string;
  my_price: string;
  part_id: string;
  part_type: string;
  price_list_id: string;
  price_type: string;
  priority: string;
  retail_price: string;
  tier_1_savings: string;
  tier_2_savings: string;
  tier_3_savings: string;
  tier_4_savings: string;
};

type CompetitorPriceInfo = {
  [key: string]: PriceInfo;
};

type ComparisonPricingInfo = {
  competitorPrice: CompetitorPriceInfo;
  competitorPriceCalculation: {
    avgRetailPrice: string;
    maxRetailPrice: string;
    minRetailPrice: string;
  };
  dealerPrice: PriceInfo;
};

type PartPriceMap = {
  [partId: string]: {
    comparisonPricingInfo: ComparisonPricingInfo;
    tireInfo: TireInfo;
  };
};
